/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {UserService} from '../../../shared/services/user.service';
import {PartNumberChangesStatusEnum} from '../../models/part-number-changes-status.enum';
import {Permission} from '../../../shared/utils/permission.enum';
import {
    PartNumberChangesDetailStateModel
} from '../../part-number-changes-detail/part-number-changes-detail-state.model';
import {InputDisplayModeEnum} from '../../../shared/input/input-display-mode.enum';
import {PartNumberChangesDetailComponentsModel} from './models/part-number-changes-detail-components.model';
import {PartNumberChangesPageTypeEnum} from './models/part-number-changes-page-type.enum';
import {AvailableActions} from '../../../api/ms-partnumberchanges/contracts/available-actions.enum';
import {PartNumberChangesDisplayComponentsTypeEnum} from './models/part-number-changes-display-components-type.enum';
import PNC_DISPLAY_CONFIGURATION_CONSTANTS from './part-number-changes-display-constants';
import {
    PartNumberChangesDetailSectionsConfigurationService
} from './part-number-changes-detail-sections-configuration.service';
import {PncTypeEnum} from '../../models/pnc-type-enum/pnc-type.enum';
import {PncCreateNewState} from '../../part-number-changes-create-new/models/pnc-create-new.state';
import {PncDetailModel} from '../../models/pnc-detail/pnc-detail.model';
import {
    PartNumberChangesDetailTrialDisplayConfigurationModel
} from './models/pnc-trial/part-number-changes-detail-trial-display-configuration.model';

@Injectable({
    providedIn: 'root'
})
export class PartNumberChangesDisplayConfigurationService {
    // The dialog button should only being shown when there are more than 1 trials.
    private readonly hideDialogButton = 1;

    constructor(private userService: UserService,
                private pncDetail: PartNumberChangesDetailSectionsConfigurationService) {
    }

    retrievePNCDisplayConfiguration(pageType: PartNumberChangesPageTypeEnum,
                                    pncDetail?: PncDetailModel,
                                    state?: any,
                                    pncType?: PncTypeEnum): PartNumberChangesDetailComponentsModel[] {
        let configuration: PartNumberChangesDetailComponentsModel[] = [];

        if (pageType === PartNumberChangesPageTypeEnum.DETAIL) {
            configuration = this.retrievePNCDetailDisplayConfiguration(pncDetail, state);
        } else if (pageType === PartNumberChangesPageTypeEnum.CREATE) {
            configuration = this.retrievePNCCreateDisplayConfiguration(pncType, state);
        }

        return configuration;
    }

    private getOldPartNumberCreateDisplayConfiguration(pncType: PncTypeEnum): PartNumberChangesDetailTrialDisplayConfigurationModel {
        let configuration: PartNumberChangesDetailTrialDisplayConfigurationModel = null;
        switch (pncType) {
            case PncTypeEnum.AI_CHANGE:
                configuration = PNC_DISPLAY_CONFIGURATION_CONSTANTS.oldPartNumber.createAiChange;
                break;
            case PncTypeEnum.PARTNER:
                configuration = PNC_DISPLAY_CONFIGURATION_CONSTANTS.oldPartNumber.createPartner;
                break;
            case PncTypeEnum.PLANNER:
                configuration = PNC_DISPLAY_CONFIGURATION_CONSTANTS.oldPartNumber.createPlanner;
                break;
            case PncTypeEnum.F2_MISSING_PARTS:
                configuration = PNC_DISPLAY_CONFIGURATION_CONSTANTS.oldPartNumber.createF2MissingParts;
                break;
        }
        return configuration;
    }

    private retrievePNCCreateDisplayConfiguration(pncType: PncTypeEnum, state: PncCreateNewState):
            PartNumberChangesDetailComponentsModel[] {

        const configuration: PartNumberChangesDetailComponentsModel[] = [];

        // Credentials
        configuration.push({
            type: PartNumberChangesDisplayComponentsTypeEnum.CREDENTIALS,
            data: {
                pncType,
                displayMode: InputDisplayModeEnum.EDIT
            }
        });

        // Old part number
        configuration.push({
            type: PartNumberChangesDisplayComponentsTypeEnum.OLD_PART_NUMBER_CREATE,
            data: {
                displayConfiguration: {
                    ...this.getOldPartNumberCreateDisplayConfiguration(pncType),
                    pncType: pncType,
                    partNumberChangeConfig: state.partNumberChangesConfiguration
                }
            }
        });

        // Solution
        if (pncType === PncTypeEnum.AI_CHANGE) {
            configuration.push({
                type: PartNumberChangesDisplayComponentsTypeEnum.SECTION_TITLE,
                data: PNC_DISPLAY_CONFIGURATION_CONSTANTS.title.solution
            });
            configuration.push({
                type: PartNumberChangesDisplayComponentsTypeEnum.AI_SOLUTION,
                data: {
                    data: null,
                    displayConfiguration: {
                        ...PNC_DISPLAY_CONFIGURATION_CONSTANTS.solution.aiChange,
                        partNumberChangeConfig: state.partNumberChangesConfiguration
                    }
                }
            });
        }

        // Footer
        configuration.push({
            type: PartNumberChangesDisplayComponentsTypeEnum.FOOTER,
            data: pncType === PncTypeEnum.AI_CHANGE
                    ? PNC_DISPLAY_CONFIGURATION_CONSTANTS.footer.pcnCreateAndSolve
                    : PNC_DISPLAY_CONFIGURATION_CONSTANTS.footer.pncCreate
        });

        return configuration;
    }

    private retrievePNCDetailDisplayConfiguration(pncDetail: PncDetailModel,
                                                  state: PartNumberChangesDetailStateModel): PartNumberChangesDetailComponentsModel[] {
        const detail = this.pncDetail.retrievePNCDetailSectionsConfiguration(pncDetail, state);
        const configuration: PartNumberChangesDetailComponentsModel[] = [];

        const userPermissionsPartner = this.hasPartnerPermissions();
        const userPermissionsPlanner = this.hasPlannerPermissions();
        const userPermissionsAdmin = this.hasAdminPermissions();

        // Sticky Header
        if (this.showDetailSolutionSolvedSection(<PartNumberChangesStatusEnum>pncDetail.status.code) &&
                (userPermissionsAdmin || userPermissionsPartner || (userPermissionsPlanner && pncDetail.type.code !== PncTypeEnum.AI_CHANGE))) {
            configuration.push({
                type: PartNumberChangesDisplayComponentsTypeEnum.STICKY_HEADER,
                data: detail.stickyHeader
            });
        }

        // Header
        configuration.push({type: PartNumberChangesDisplayComponentsTypeEnum.HEADER, data: detail.header});

        // Solution (solved) section
        if (this.showDetailSolutionSolvedSection(<PartNumberChangesStatusEnum>pncDetail.status.code)) {
            // Title
            configuration.push({
                type: PartNumberChangesDisplayComponentsTypeEnum.SECTION_TITLE,
                data: PNC_DISPLAY_CONFIGURATION_CONSTANTS.title.solution
            });
            // Data
            configuration.push({
                type: pncDetail.type?.code === PncTypeEnum.AI_CHANGE ?
                        PartNumberChangesDisplayComponentsTypeEnum.AI_SOLUTION : PartNumberChangesDisplayComponentsTypeEnum.SOLUTION,
                data: detail.solution
            });
        }

        // Cancelled section
        if (this.showDetailCancelledSection(<PartNumberChangesStatusEnum>pncDetail.status.code)) {
            // Title
            configuration.push({
                type: PartNumberChangesDisplayComponentsTypeEnum.SECTION_TITLE,
                data: PNC_DISPLAY_CONFIGURATION_CONSTANTS.title.cancellation
            });

            // Data
            configuration.push({
                type: PartNumberChangesDisplayComponentsTypeEnum.CANCELLED,
                data: detail.cancellation
            });
        }

        // Credentials section
        // Data: Credentials, Title and Timeline
        configuration.push({
            type: PartNumberChangesDisplayComponentsTypeEnum.CREDENTIALS,
            data: detail.credentials
        });

        // Old part number credentials
        // Title
        configuration.push({
            type: PartNumberChangesDisplayComponentsTypeEnum.SECTION_TITLE,
            data: PNC_DISPLAY_CONFIGURATION_CONSTANTS.title.oldPartNumber
        });

        // Dialog history button
        if (detail.oldPartNumberCredentials?.trials?.length > this.hideDialogButton) {
            configuration.push({
                type: PartNumberChangesDisplayComponentsTypeEnum.DIALOG_HISTORY_BUTTON,
                data: PNC_DISPLAY_CONFIGURATION_CONSTANTS.title.dialogHistoryTrials
            });
        }

        // Trials
        if (detail.oldPartNumberCredentials?.trials != null) {
            configuration.push({
                type: PartNumberChangesDisplayComponentsTypeEnum.TRIALS,
                data: detail.oldPartNumberCredentials.trials
            });
        }
        // Current
        if (detail.oldPartNumberCredentials?.showCurrent) {
            configuration.push({
                type: PartNumberChangesDisplayComponentsTypeEnum.CURRENT_TRIAL,
                data: detail.oldPartNumberCredentials.current
            });
        }

        if (detail.solution?.requiresFlashing) {
            // Title
            configuration.push({
                type: PartNumberChangesDisplayComponentsTypeEnum.SECTION_TITLE,
                data: PNC_DISPLAY_CONFIGURATION_CONSTANTS.title.solution
            });

            // Solution trials data
            configuration.push({
                type: PartNumberChangesDisplayComponentsTypeEnum.SOLUTION_TRIALS,
                data: detail.solution.solutionTrials
            });

            // Solution data form
            if (pncDetail.status.code === PartNumberChangesStatusEnum.EE_ANALYSIS) {
                configuration.push({type: PartNumberChangesDisplayComponentsTypeEnum.SOLUTION, data: detail.solution});
            }

            if (pncDetail.status.code === PartNumberChangesStatusEnum.SOLVED) {
                const info = {...detail.solution, isSolutionEE: true};
                configuration.push({type: PartNumberChangesDisplayComponentsTypeEnum.SOLUTION, data: info});
            }
        }

        // Solution section FORM for ANALYSIS
        if (pncDetail.status.code === PartNumberChangesStatusEnum.ANALYSIS && state.isSolving && this.showSolutionForm()) {
            if (detail.solution?.solutionTrials?.length === 0) {
                // Title
                configuration.push({
                    type: PartNumberChangesDisplayComponentsTypeEnum.SECTION_TITLE,
                    data: PNC_DISPLAY_CONFIGURATION_CONSTANTS.title.solution
                });
            }
            configuration.push({type: PartNumberChangesDisplayComponentsTypeEnum.SOLUTION, data: detail.solution});
        }

        // Footer
        if (pncDetail.availableActions?.includes(AvailableActions.REJECT)
                || pncDetail.availableActions?.includes(AvailableActions.SEND)
                || pncDetail.availableActions?.includes(AvailableActions.REJECT_SOLUTION)
                || pncDetail.availableActions?.includes(AvailableActions.SEND_SOLUTION)) {
            configuration.push({type: PartNumberChangesDisplayComponentsTypeEnum.FOOTER, data: detail.footer});
        }

        return configuration;
    }

    /**
     * Returns false if the user has EE_SPECIALIST_PNC_VIEW, EE_SPECIALIST_PNC_ACCEPT or EE_SPECIALIST_PNC_REJECT permissions
     * and the user should not see the solution form.
     */
    private showSolutionForm(): boolean {
        return this.userService.hasPermissions([
            Permission.TECHNICIAN_PNC_SOLVE
        ]);
    }

    /**
     * For a given PCN detail returns if the solution section (with solved status) should be showed
     * @param status the {@link PartNumberChangesStatusEnum} of the PNC
     */
    private showDetailSolutionSolvedSection(status: PartNumberChangesStatusEnum): boolean {
        return status === PartNumberChangesStatusEnum.SOLVED && this.userService.hasPermissions(
                [
                    Permission.TECHNICIAN_PNC_VIEW,
                    Permission.PARTNER_PNC_VIEW,
                    Permission.PLANNER_PNC_VIEW,
                    Permission.PRICE_PLANNER_PNC_VIEW,
                    Permission.ADMIN_PNC_VIEW,
                    Permission.EE_SPECIALIST_PNC_VIEW
                ]);
    }

    /**
     * For a given PCN detail returns if the cancelled section (with cancelled status) should be shown
     * @param status the {@link PartNumberChangesStatusEnum} of the PNC
     */
    private showDetailCancelledSection(status: PartNumberChangesStatusEnum): boolean {
        return status === PartNumberChangesStatusEnum.CANCELLED && this.userService.hasPermissions(
                [
                    Permission.PARTNER_PNC_VIEW,
                    Permission.PLANNER_PNC_VIEW,
                    Permission.ADMIN_PNC_VIEW,
                    Permission.PRICE_PLANNER_PNC_VIEW,
                    Permission.TECHNICIAN_PNC_VIEW
                ]);
    }

    private hasPlannerPermissions(): boolean {
        return this.userService.hasPermissions([
            Permission.PLANNER_PNC_CREATE,
            Permission.PLANNER_PNC_VIEW,
            Permission.PLANNER_PNC_SEND
        ]);
    }

    private hasPartnerPermissions(): boolean {
        return this.userService.hasPermissions([
            Permission.PARTNER_PNC_CREATE,
            Permission.PARTNER_PNC_VIEW,
            Permission.PARTNER_PNC_SEND
        ]);
    }

    private hasAdminPermissions(): boolean {
        return this.userService.hasPermissions([
            Permission.ADMIN_PNC_VIEW
        ]);
    }
}
